import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type {
  PAYMENT_METHOD_CREDIT_CARD,
  PAYMENT_METHOD_DIRECT_DEBIT,
  PaymentMethod,
} from 'frontend-payments/utils/constants/payment-method-constants';
import type { CountryCode } from 'frontend-utils/utils/countries';
import Countries from 'frontend-utils/utils/countries';

interface Billing {
  city: string | null;
  country: CountryCode;
  frequency: string;
  legalEntityName: string;
  state: string | null;
  street: string;
  vatNumber: string | null;
  zipCode: string | null;
}

interface BankDetails {
  accountName: string;
  accountNumber?: string;
  address: string;
  bankName: string;
  iban?: string;
  routingNumber?: string;
  swiftCode: string;
}

interface CheckDetails {
  address: string;
  bankName: string;
}

interface RecurringPaymentMethod {
  brand: string;
  id: string;
  lastDigits: string;
  name: string;
  type: typeof PAYMENT_METHOD_DIRECT_DEBIT | typeof PAYMENT_METHOD_CREDIT_CARD;
}

interface Payments {
  availablePaymentMethods: PaymentMethod[];
  bankDetails?: BankDetails;
  checkDetails?: CheckDetails;
  recurringPaymentMethod: RecurringPaymentMethod | null;
}

export default class Account extends Model {
  [Type] = 'account' as const;

  @attr('string') declare accountNumber: string;

  @attr('string') declare balance: string;
  @attr('json') declare billing: Billing;
  @attr('string') declare currency: string;
  @attr('string') declare name: string;
  @attr('json') declare payments: Payments;

  @service declare intl: Services['intl'];

  private countries: Countries = new Countries(getOwner(this)!);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countryName() {
    return this.countries.getName(this.billing.country);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedActivePaymentMethod() {
    if (!this.payments.recurringPaymentMethod) {
      return this.intl.t('payments.payment_method.not_set_up');
    }

    return this.payments.recurringPaymentMethod.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDetailsText() {
    const address = [
      this.billing.street,
      this.billing.city,
      this.billing.zipCode,
      this.billing.state,
      this.countryName,
    ]
      .filter(Boolean)
      .join(', ');

    const vat = this.billing.vatNumber
      ? `${this.intl.t('payments.billing_details.vat')}: ${this.billing.vatNumber}`
      : null;

    return [address, vat].filter(Boolean).join(' - ');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedFrequency() {
    if (!this.billing.frequency) {
      return this.intl.t('general.missing');
    }

    return this.billing.frequency;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLastFourDigits() {
    const digits = this.payments.recurringPaymentMethod?.lastDigits;

    if (!digits) {
      return '';
    }
    return `(***${digits})`;
  }
}
